import { useState, type FormEvent } from 'react';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { ROTAS } from '@remoto/shared';
import { rotaRaiz } from './raiz';
import { chamar } from '../api';
import { Aviso, Botao, Campo, Cartao } from '../componentes/campos';
import { mensagemDoErro } from '../erros';

/**
 * A página que o link do e-mail abre. Filha da raiz, como `/entrar`: existe
 * sem sessão — quem chega aqui esqueceu justamente a credencial.
 */
function RedefinirSenha(): React.JSX.Element {
  const navegar = useNavigate();
  const { token } = rotaRedefinirSenha.useSearch();
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [pronto, setPronto] = useState(false);

  const enviar = (evento: FormEvent) => {
    evento.preventDefault();
    setErro(null);
    if (senha !== confirmacao) {
      setErro('As duas senhas não são iguais. Digite a mesma nas duas caixas.');
      return;
    }
    setOcupado(true);
    void chamar<{ ok: boolean }>(ROTAS.auth.senhaRedefinir, {
      method: 'POST',
      corpo: { token, novaSenha: senha },
      semRenovar: true,
    })
      .then(() => setPronto(true))
      .catch((e) => setErro(mensagemDoErro(e, 'Não foi possível redefinir a senha.')))
      .finally(() => setOcupado(false));
  };

  if (pronto) {
    return (
      <Cartao
        titulo="Senha redefinida"
        descricao="A senha nova já vale. Entre com ela — e, se a conta tem segundo fator, ele continua o mesmo."
      >
        <Botao type="button" onClick={() => void navegar({ to: '/entrar' })}>
          Ir para a entrada
        </Botao>
      </Cartao>
    );
  }

  return (
    <Cartao
      titulo="Redefinir a senha"
      descricao="Escolha a senha nova desta conta. O link do e-mail vale uma vez só."
    >
      <form onSubmit={enviar} className="space-y-4">
        <Campo
          rotulo="Senha nova"
          type="password"
          autoComplete="new-password"
          dica="Pelo menos 12 caracteres."
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          required
          minLength={12}
          autoFocus
        />
        <Campo
          rotulo="Repita a senha nova"
          type="password"
          autoComplete="new-password"
          value={confirmacao}
          onChange={(e) => setConfirmacao(e.target.value)}
          required
        />
        {erro ? <Aviso>{erro}</Aviso> : null}
        <Botao carregando={ocupado}>Redefinir</Botao>
      </form>
    </Cartao>
  );
}

export const rotaRedefinirSenha = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/redefinir-senha',
  validateSearch: (busca: Record<string, unknown>) => ({
    token: typeof busca['token'] === 'string' ? busca['token'] : '',
  }),
  component: RedefinirSenha,
});
